import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Loader2, Users, Kanban, Building2, Mail, Send, Download } from "lucide-react";
import { adminApi, clearAdminToken } from "@/lib/adminApi";
import { LOGO_URL } from "@/lib/content";

const CSV_HEADERS = [
  "email",
  "first_name",
  "last_name",
  "phone",
  "source",
  "venue_name",
  "venue_slug",
  "venue_id",
  "created_at",
  "unsubscribed",
  "unsubscribed_at",
];

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  // If value contains comma / quote / newline / carriage return — wrap in quotes
  if (/[",\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

function downloadContactsCsv(rows) {
  const lines = [CSV_HEADERS.join(",")];
  for (const r of rows) {
    lines.push(
      CSV_HEADERS.map((h) => {
        if (h === "unsubscribed") return r.unsubscribed ? "Yes" : "No";
        return csvEscape(r[h]);
      }).join(",")
    );
  }
  // BOM for Excel UTF-8 compatibility
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `swingle-contacts-${stamp}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export default function AdminContacts() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [runningWeekly, setRunningWeekly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/contacts");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        r.email.toLowerCase().includes(s) ||
        (r.first_name || "").toLowerCase().includes(s) ||
        r.venue_name.toLowerCase().includes(s)
    );
  }, [q, rows]);

  const byVenue = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.venue_id, (m.get(r.venue_id) || 0) + 1));
    return m;
  }, [rows]);

  const runWeeklyNow = async () => {
    if (!window.confirm("Send the weekly digest now to every venue's contacts?")) return;
    setRunningWeekly(true);
    try {
      const { data } = await adminApi.post("/admin/emails/run-weekly-now");
      const total = (data.venues || []).reduce((s, v) => s + (v.sent || 0), 0);
      toast.success(`Weekly digest run — ${total} email(s) sent.`);
    } catch {
      toast.error("Failed to run weekly digest.");
    } finally {
      setRunningWeekly(false);
    }
  };

  const logout = () => {
    clearAdminToken();
    nav("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-contacts-page" className="min-h-screen bg-black text-[#F5F5F0]">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-8 flex items-center justify-between h-20 gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <Link to="/admin">
              <img src={LOGO_URL} alt="Swingle" className="h-12 w-auto object-contain" />
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              <Link to="/admin" className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors">
                <Kanban size={14} /> Pipeline
              </Link>
              <Link to="/admin/venues" className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors">
                <Building2 size={14} /> Venues
              </Link>
              <Link to="/admin/contacts" className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]">
                <Users size={14} /> Contacts
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={runWeeklyNow}
              disabled={runningWeekly}
              data-testid="admin-run-weekly-now"
              className="inline-flex items-center gap-2 border border-[#D9A6F5]/40 text-[#D9A6F5] hover:bg-[#D9A6F5] hover:text-black disabled:opacity-40 px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              {runningWeekly ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Run Weekly Digest Now
            </button>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#D9A6F5] px-3 py-2 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Aggregated Contacts</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
              {loading ? "Loading…" : `${rows.length} contacts · ${byVenue.size} venue${byVenue.size === 1 ? "" : "s"}`}
            </h1>
            <p className="mt-2 text-sm text-[#A3A3A3]">
              Audit-only record of every contact any venue has captured, tagged with source and
              venue. Kept in case of a legal/regulatory request. Not used for sending.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              disabled={loading || rows.length === 0}
              onClick={() => downloadContactsCsv(rows)}
              data-testid="admin-contacts-download-csv"
              className="inline-flex items-center gap-2 border border-[#D9A6F5] text-[#D9A6F5] hover:bg-[#D9A6F5] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Download size={12} /> Download CSV
              {rows.length > 0 && (
                <span className="text-[10px] opacity-70">({rows.length})</span>
              )}
            </button>
            <input
              type="search"
              placeholder="Search email, name or venue…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-contacts-search"
              className="bg-black border border-white/15 h-11 px-3 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5] w-full md:w-80"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-white/10">
            <div className="grid grid-cols-[1.5fr_1fr_1.5fr_auto] gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.22em] text-[#D9A6F5]">
              <div>Email</div>
              <div>First name</div>
              <div>Venue</div>
              <div>Added</div>
            </div>
            {filtered.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-contact-row-${r.id}`}
                className="grid grid-cols-[1.5fr_1fr_1.5fr_auto] gap-4 px-5 py-3 border-b border-white/5 last:border-b-0 text-sm items-center"
              >
                <div className="text-[#F5F5F0] truncate flex items-center gap-2">
                  <Mail size={11} className="text-[#D9A6F5]" /> {r.email}
                </div>
                <div className="text-[#A3A3A3] truncate">{r.first_name || "—"}</div>
                <div className="text-[#F5F5F0] truncate">
                  <span className="inline-block px-2 py-0.5 border border-[#D9A6F5]/40 text-[10px] uppercase tracking-[0.18em] text-[#D9A6F5]">
                    {r.venue_name}
                  </span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                  {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-5 py-16 text-center text-[#A3A3A3]">No contacts match.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
